import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton.jsx";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import { graphqlEndpoint } from "../../config";
import { CREATE_DEPARTMENT } from "../../graph/departments/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const validationSchema = yup.object({
  name: yup.string().trim().required("Department name is required"),
  description: yup.string().nullable(),
});

const CreateDepartmentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const redirectPath = location.state?.redirectPath || "/manageDepartments";

  const initialValues = {
    name: "",
    description: "",
  };

  const handleSubmit = async (values) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select a tenant before creating a department",
      });
      return;
    }

    const input = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      tenantId,
    };

    try {
      await client.request(CREATE_DEPARTMENT, { input });
      setSnackbar({
        open: true,
        severity: "success",
        message: "Department created successfully",
      });
      navigate(redirectPath);
    } catch (err) {
      console.error("Failed to create department:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to create department",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CREATE DEPARTMENT"
          subtitle="Select a tenant before creating a department"
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="CREATE DEPARTMENT"
        subtitle={`Create department for ${tenantName || "selected tenant"}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Department Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Create Department
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateDepartmentForm;